/**
 * pasteFormula.js — raw pasted TEXT → a formula the S1-P3 grammar actually accepts (TEXT ⇄ BLOCKS).
 *
 * The Formula editor's compiled bar became EDITABLE so an author can have an LLM write a formula
 * outside the app (against `POC-Calc-Blueprint.md`) and paste it straight in. What an LLM emits is
 * never quite what `formulaGraph.tokenize` accepts — the blueprint itself writes
 *
 *     expense_40_5a = if([USE_ACTUAL_40_5A] = 1, [EXP_40_5A_ACTUAL], [INC_40_5A] * 0.30)
 *
 * i.e. a leading `name =` assignment and `[IDENT]` brackets, BOTH of which are a hard
 * "unexpected character" today. So this module sits in front of the parser: it REPAIRS what can be
 * repaired mechanically, and turns what cannot into a Thai sentence an admin can act on — never a
 * raw `parse error at 47`, which tells the reader nothing they can fix.
 *
 * PURE ESM, HEADLESS (`node tests/pasteFormula.test.mjs`). Blockly ONLY through `./blocklyCore.js`.
 *
 * ── The rules this file is built around ───────────────────────────────────────────────────
 * 1) EVERY repair is REPORTED. A silent rewrite of an author's formula is indistinguishable from a
 *    bug: the text they read back is not the text they wrote, and they find out at calculation time.
 * 2) NO transformation may reach INSIDE a single-quoted literal without SAYING so. The literal charset
 *    (formulaGraph `isSafeStringLiteral`) legitimately allows `-`, `.`, `/`, Thai combining marks and
 *    full-width letters/digits — so a blind `[ ] → ` or full-width→ASCII sweep silently rewrites a
 *    rule label into a DIFFERENT value the engine then never matches. This is the exact bug class the
 *    codebase already fights twice: `normalizeExpr` (ncalcToBlocks) and `ExtractAndValidateLiterals`
 *    (the C# evaluator) both mask literals before scanning. We split into segments and map only the
 *    outside — except for the invisible characters, which are stripped INSIDE too because nobody can
 *    see them, and that one exception names the literal it changed.
 * 3) A WRONG ANSWER THAT LOOKS RIGHT IS THE WORST OUTCOME. Anything ambiguous is refused, loudly, with
 *    the construct named and the spelling to use instead. In particular: `ok:true` means the text
 *    compiles through the SAME anchor the apply path uses (`tax_formula_result` →
 *    `workspaceToExpression`), and the canonical it returns is re-parsed to prove the editor can open
 *    what it is about to persist. A canonical this module cannot re-read is a formula nobody can.
 *
 * The grammar we are repairing TOWARD is exactly what `tokenize`/`parseExpression` accept:
 *   numbers · 'single-quoted text' · identifiers · nested PATHS (`A.b[].c`) · + - * / ( ) , ·
 *   < <= > >= == != = <> · MIN MAX ROUND IF IFS SUM — and nothing else. No computed index, no " " on
 *   the way OUT, no && || ?:, no % ^, no method call on a path.
 */
import Blockly from './blocklyCore.js'
import { TAX_FORMULA_RESULT_TYPE } from './taxBlocks.js' // import side effect: registers the tax_* defs
import {
  parseExpression,
  tokenize,
  IFS_FN,
  SUM_FN,
  isRetiredFunction,
  isRetiredIdentifier,
  retiredFunctionMessage,
  retiredIdentifierMessage,
  analyzeIteration,
  pathRoot,
} from '../formulaGraph.js'
import { MAX_EXPRESSION_LEN, workspaceToExpression } from './ncalcGenerator.js'
import { ncalcToBlocks } from './ncalcToBlocks.js'

/** The ONLY functions the sandbox knows. Spelled once — every message quotes this list. */
export const ALLOWED_FUNCTIONS = ['MIN', 'MAX', 'ROUND', 'IF', IFS_FN, SUM_FN]
const ALLOWED_FN_SET = new Set(ALLOWED_FUNCTIONS)
const FN_LIST_TH = ALLOWED_FUNCTIONS.join(', ')

// ──────────────────────────────────────────────────────────────────────────────
//  Literal-safe segmentation (the rule-2 machinery)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Split `s` into alternating {lit:false} / {lit:true} segments, where a `lit` segment is a complete
 * single-quoted literal INCLUDING its quotes.
 *
 * "The next quote closes the literal" is the same rule `tokenize` uses, and it is safe for the same
 * reason: the literal grammar has no backslash and no escape sequence, so a quote can never appear
 * inside one. An UNTERMINATED quote is deliberately NOT salvaged — the rest of the text is left in a
 * non-lit segment so the repairs still run and the parser gets to report the real problem.
 * @param {string} s
 * @returns {Array<{lit:boolean, text:string}>}
 */
function splitLiterals(s) {
  const segs = []
  let buf = ''
  let i = 0
  while (i < s.length) {
    if (s[i] !== "'") { buf += s[i]; i += 1; continue }
    const close = s.indexOf("'", i + 1)
    if (close === -1) { buf += s.slice(i); break } // unterminated — not a literal, hand it to the parser
    segs.push({ lit: false, text: buf })
    segs.push({ lit: true, text: s.slice(i, close + 1) })
    buf = ''
    i = close + 1
  }
  segs.push({ lit: false, text: buf })
  return segs
}

/** Apply `fn` to everything OUTSIDE single-quoted literals; literals pass through byte-identical. */
function mapOutside(s, fn) {
  return splitLiterals(s).map((g) => (g.lit ? g.text : fn(g.text))).join('')
}

/**
 * Apply `fn` INSIDE every single-quoted literal (content only, quotes kept) and report each literal
 * that actually changed. The ONLY transformations allowed through here are the invisible characters:
 * they are illegal literal content, so leaving them would be a parse error quoting a literal that
 * LOOKS perfect on screen — the most baffling message this module can produce. Everything else stays
 * outside-only (rule 2).
 * @returns {{text:string, changed:Array<{from:string,to:string}>}}
 */
function mapInsideLiterals(s, fn) {
  const changed = []
  const text = splitLiterals(s).map((g) => {
    if (!g.lit) return g.text
    const inner = g.text.slice(1, -1)
    const next = fn(inner)
    if (next !== inner) changed.push({ from: inner, to: next })
    return "'" + next + "'"
  }).join('')
  return { text, changed }
}

/** The text with every literal blanked to spaces (quotes kept, so offsets do NOT shift). */
function maskLiterals(s) {
  return splitLiterals(s)
    .map((g) => (g.lit ? "'" + ' '.repeat(g.text.length - 2) + "'" : g.text))
    .join('')
}

// ──────────────────────────────────────────────────────────────────────────────
//  Repair table
// ──────────────────────────────────────────────────────────────────────────────

// Invisible characters. These are the reason a paste out of a browser/Word/Notion dies with
// `unexpected character " "` pointing at something the author cannot SEE — the single most baffling
// failure in this whole path, and the cheapest to fix.
const INVISIBLE_TO_SPACE = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g
const INVISIBLE_TO_EMPTY = /[\u200B-\u200D\u2060\uFEFF\u00AD]/g

// EVERY quote-shaped glyph, folded to its ASCII twin BEFORE literal segmentation: a paste whose
// literal is wrapped in ‘ ’ (or in the full-width ＇ / ＂) has no straight quote at all yet, so
// segmentation would see no literal to protect and every "outside only" repair would run INSIDE what
// later becomes the literal. U+FF07 used to be folded LATER, with the rest of the full-width block,
// and that one-line gap was enough for the [ ] and dash repairs to rewrite literal content silently.
// A new quote glyph belongs HERE, never in FULLWIDTH_ASCII.
const SMART_SINGLE = /[\u2018\u2019\u201A\u201B\u2032\uFF07]/g
const SMART_DOUBLE = /[\u201C\u201D\u201E\u201F\u2033\uFF02]/g

// Operator look-alikes (outside literals only — `-` and `.` are legal literal content).
const DASH_TO_MINUS = /[\u2212\u2010\u2011\u2012\u2013\u2014\u2015]/g
const TIMES_TO_STAR = /[\u00D7\u2217\u22C5\u2219]/g
const DIVIDE_TO_SLASH = /[\u00F7\u2215]/g

// Full-width ASCII forms （）＋－＊／，＝＜＞ and Ａ-Ｚ ａ-ｚ ０-９ ＿ are one contiguous block
// (U+FF01–U+FF5E) whose ASCII twin is exactly `cp - 0xFEE0`. One rule instead of a hand-kept table —
// a table is how ＿ or ０ gets forgotten and produces the same invisible failure as an NBSP.
// The two quote forms in that block (＇ ＂) are folded EARLIER, with the other quote glyphs.
const FULLWIDTH_ASCII = /[\uFF01-\uFF5E]/g

// `[INC_40_1]` — the POC-Calc-Blueprint's variable notation, rewritten to a bare name.
// The LOOKBEHIND is load-bearing now that `[]` is a path's iteration marker: without it this repair
// eats the brackets of `a.items[i]` and hands back `a.itemsi` — a variable nobody wrote, ok:true,
// green preview. A blueprint bracket always follows an operator, a paren, a comma or the start of the
// text; a path bracket always follows a segment NAME, and that is exactly what is excluded here.
const BRACKET_IDENT_RE = /(?<![A-Za-z0-9_\]])\[\s*([A-Za-z_][A-Za-z0-9_]*)\s*\]/g
const IDENT_G = /[A-Za-z_][A-Za-z0-9_]*/g
// A line whose FIRST token is `name =` — the shape of an assignment head. Only meaningful at paren
// depth 0 (see topLevelAssignmentHeads): the same shape one level in is an EQ comparison argument.
const LINE_ASSIGN_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*=(?![=<>])/

// ── the numeric comma (see repairNumericCommas) ───────────────────────────────
// A comma between two NUMBERS is the single most dangerous character in a pasted formula: MIN/MAX are
// variadic, so `MIN(x, 100,000)` parses CLEANLY as MIN(x, 100, 0), the cap evaluates to zero forever
// and nothing anywhere objects. It cannot be handled by a text regex over the raw paste, because the
// same reading survives every whitespace spelling (`100 ,000`, `100, 000`, `100,\n000`) and the
// whitespace collapse runs LATER — a whitespace-intolerant pattern is one space away from useless.
// So the pairs are found POSITIONALLY (number token, comma-only gap, number token) and classified.
const GROUP_PREFIX = /^\d{1,3}$/      // a leading digit group, or the continuation group of a chain
const GROUP_THREE = /^\d{3}$/         // a thousands group
const LEADING_ZERO_RUN = /^0\d+$/     // `000`, `05` — never a standalone argument anybody writes
const TWO_DIGITS = /^\d{2}$/          // the tail of a European decimal (`0,30`)
const COMMA_ONLY_GAP = /^\s*,\s*$/    // whitespace-TOLERANT on purpose: that is the whole point
// `300_000` — code-style digit grouping. Anchored on both sides so `inc_40_8_perf` (an identifier,
// which starts with a letter) can never match.
const NUMERIC_UNDERSCORE = /(?<![A-Za-z0-9_])\d{1,3}(?:_\d{3})+(?![A-Za-z0-9_])/g
// `5e6` — exponent form. Same anchoring; the expansion is exact or it is refused (see NUMBER_RANGE).
const SCI_NOTATION = /(?<![A-Za-z0-9_.])(\d+(?:\.\d+)?)[eE]([+-]?\d+)(?![A-Za-z0-9_])/g
// Every numeric literal, for the range/precision gate. `1e21` and `0.0000001` are the two shapes that
// make the GENERATOR emit exponent notation, which this project's own tokenizer cannot read back.
const NUMBER_TOKEN = /(?<![A-Za-z0-9_.])\d*\.?\d+/g

/** Every NUMBER token in `masked`, with its offsets. (Masking pads, so offsets index `s` too.) */
function numberTokens(masked) {
  const out = []
  NUMBER_TOKEN.lastIndex = 0
  let m
  while ((m = NUMBER_TOKEN.exec(masked)) !== null) {
    out.push({ text: m[0], start: m.index, end: m.index + m[0].length })
  }
  return out
}

/**
 * For every offset in `masked`: could a comma sitting there be an ARGUMENT SEPARATOR?
 *
 * In this grammar a comma is legal in exactly ONE position — between the arguments of a function call.
 * So a comma whose innermost enclosing paren is NOT a function-call paren (`(1,234)`), or which sits
 * at paren depth 0 (`gross_income - 100,000`, a bare `4,699,396`), has no separator reading AT ALL:
 * the only way that text can ever be a formula is as one grouped number. That is what makes joining
 * it a FACT rather than a guess, and it is the only disambiguation this module is allowed to make on
 * a tight comma (see repairNumericCommas — everything else that is genuinely two-way is refused).
 *
 * A "function-call paren" is one whose preceding non-space character is an identifier character.
 * `MIN (a, b)` counts (the space is legal); `(a + b)` and `((x))` do not.
 * @param {string} masked  literal-masked text (masking pads, so offsets line up with the original)
 * @returns {(index:number)=>boolean}
 */
function separatorContext(masked) {
  const flags = new Array(masked.length).fill(false)
  const stack = []
  for (let i = 0; i < masked.length; i += 1) {
    const c = masked[i]
    if (c === '(') {
      let j = i - 1
      while (j >= 0 && /\s/.test(masked[j])) j -= 1
      stack.push(j >= 0 && /[A-Za-z0-9_]/.test(masked[j]))
      continue
    }
    if (c === ')') { stack.pop(); continue }
    flags[i] = stack.length > 0 && stack[stack.length - 1] === true
  }
  return (i) => (i >= 0 && i < flags.length ? flags[i] : false)
}

/**
 * Classify — and only then repair — every comma that sits between two NUMBERS.
 *
 * The pair is found by POSITION (`number , number`, any whitespace, newline included) rather than by a
 * character pattern, because every whitespace spelling reads the same to the parser and a
 * whitespace-intolerant regex silently lets `100 ,000` through as two arguments. The four verdicts:
 *
 *   GROUPING    `100,000` `100 , 000` `1,000,000` `4,699,396` → join the digits, REPORT it.
 *               Two independent proofs are accepted, and EITHER is enough — but "the comma is TIGHT"
 *               is NOT one of them (see AMBIGUOUS):
 *                 • the right group has a LEADING ZERO (`,000`) — `000` is not a number anybody passes
 *                   as an argument, so this is a grouped number however it is spaced; or
 *                 • the comma has NO separator reading at all, because it is not inside a function
 *                   call's parens (`gross_income - 100,000`, a bare `4,699,396`, `(1,234)`). A comma
 *                   is legal ONLY between arguments, so grouping is the one reading that can ever
 *                   produce a formula — joining is then a fact about the grammar, not a guess.
 *               A chain continues on the right group (`1`,`000`,`000`), so 4+ groups work — the old
 *               `/g` regex consumed its own right group and left `1000,000000` behind.
 *   AMBIGUOUS   `MIN(a,100,200)` `MAX(500,300)` `IF(x>1,100,200)` `MIN(a, 1,234,567,890)` → a TIGHT
 *               comma inside a function call, before a 3-digit group with no leading zero. REFUSED.
 *               A tight comma used to be accepted as proof of digit grouping, and that is the one
 *               reading a tight comma does NOT prove: it is also the commonest no-space spelling of an
 *               argument separator. `MIN(a,100,200)` — a perfectly good 3-argument MIN — was silently
 *               rewritten to `MIN(a, 100200)`, i.e. a cap the author never wrote, previewing green.
 *               No heuristic can read intent here, so the author is asked, in the two spellings that
 *               are not ambiguous (`100200` · `100, 200`).
 *   DECIMAL     `0,30` tight  → a European decimal comma. Stripping it is a 100× error and keeping it
 *               makes MIN(a, 0,30) three arguments, so it is REFUSED, never guessed.
 *   MIS-GROUPED `1,00` `100,0000` `12345,000` `0.5,000` → a leading-zero run that is not a legal group,
 *               or a legal-looking group after something that cannot be a group prefix. REFUSED.
 *   SEPARATOR   `MIN(1,2)` `IF(a>1,1,0)` `IF(x=1,60000,0)` `ROUND(1.5,2)` `MAX(0, 150)` → an ordinary
 *               argument comma. Left EXACTLY as written and NOT reported — it is the commonest correct
 *               spelling an LLM emits, and refusing it (the previous `/\d,\d/` guard did) is a
 *               false accusation about a number the author wrote correctly.
 *
 * THE ONE THING THIS DELIBERATELY DOES NOT DO, so it is not a hidden assumption: a SPACED comma before
 * a 3-digit group with no leading zero (`MIN(a, 19 , 500)`, `MIN(x, 1,\n234, 567)`) stays a separator.
 * It is not read as 19500 because the identical shape is how everyone writes an ordinary argument list
 * — `MAX(0, 150)`, `IF(a > 1, 100, 0)`, `IF(has_spouse = 1, 600, 300)` — and joining those would be the
 * B3 false-positive class rebuilt on the other side. Nothing is altered in that case: the text is kept
 * exactly as typed and reads exactly as the parser reads it, which is not the silent-rewrite class.
 * The realistic hazard is fully covered anyway, because every cap in Thai personal income tax ends in
 * `000` (100,000 · 60,000 · 25,000 · 9,000 · 500,000) and a `,000` group is repaired at ANY spacing.
 *
 * @returns {{text:string, repair:?{code,message}, problems:Array<{code,message}>}}
 */
function repairNumericCommas(s) {
  const masked = maskLiterals(s)
  const toks = numberTokens(masked)
  const canSeparate = separatorContext(masked)
  const joins = []
  const problems = []
  const quote = (a, b) => `"${s.slice(a.start, b.end).replace(/\s+/g, ' ')}"`

  for (let i = 0; i + 1 < toks.length; i += 1) {
    const a = toks[i]
    const b = toks[i + 1]
    const gap = masked.slice(a.end, b.start)
    if (!COMMA_ONLY_GAP.test(gap)) continue
    const tight = gap === ','
    const prefix = GROUP_PREFIX.test(a.text)
    const zeroRun = LEADING_ZERO_RUN.test(b.text)
    // Where the comma itself is — the separator reading only exists inside a call's argument list.
    const argList = canSeparate(a.end + gap.indexOf(','))

    if (GROUP_THREE.test(b.text) && prefix && (zeroRun || !argList)) {
      joins.push({ i, a, b, zeroRun })
    } else if (GROUP_THREE.test(b.text) && prefix && tight) {
      // Both readings are real and both are ordinary. Guessing either one fabricates a tax number.
      const joined = a.text + b.text
      problems.push({
        code: 'AMBIGUOUS_COMMA',
        message: `ตัวเลข ${quote(a, b)} อ่านได้ 2 แบบ และระบบไม่เดาให้ — `
          + `ถ้าเป็นตัวเลขหลักพัน ให้เขียนติดกันเป็น ${joined} · `
          + `ถ้าเป็นสองค่าคนละอาร์กิวเมนต์ ให้เว้นวรรคหลังจุลภาค (${a.text}, ${b.text})`,
      })
    } else if (zeroRun) {
      problems.push({
        code: 'NUMBER_COMMA',
        message: `ตัวเลข ${quote(a, b)} อ่านเป็นตัวคั่นหลักพันไม่ได้ — ตัวคั่นหลักพันต้องเป็นกลุ่มละ 3 หลักพอดี `
          + 'และกลุ่มแรกต้องไม่เกิน 3 หลัก (1,234,567) หรือจะเขียนติดกันไปเลยก็ได้ (1234567) — '
          + `ส่วน "${b.text}" จะเป็นอาร์กิวเมนต์เดี่ยวก็ไม่ได้ เพราะตัวเลขไม่ขึ้นต้นด้วย 0`,
      })
    } else if (tight && prefix && TWO_DIGITS.test(b.text)) {
      problems.push({
        code: 'DECIMAL_COMMA',
        message: `ตัวเลข ${quote(a, b)} ใช้จุลภาคเป็นจุดทศนิยมแบบยุโรป — ทศนิยมในสูตรนี้ต้องใช้จุด `
          + `(${a.text}.${b.text}) ถ้าตั้งใจให้เป็นสองอาร์กิวเมนต์ ให้เว้นวรรคหลังจุลภาค (${a.text}, ${b.text})`,
      })
    }
    // else: an ordinary argument separator. Nothing changed, so nothing is said.
  }

  // One unreadable number blocks the whole paste: repairing the OTHER numbers first would hand back a
  // formula that is half-repaired and still wrong, and the repair list would read like a success.
  if (problems.length) return { text: s, repair: null, problems }
  if (!joins.length) return { text: s, repair: null, problems }

  // Chains, for the report: consecutive joins that share a token are ONE number to the author.
  const chains = []
  joins.forEach((j) => {
    const last = chains[chains.length - 1]
    if (last && last[last.length - 1].i === j.i - 1) last.push(j)
    else chains.push([j])
  })
  const shown = chains.map((ch) => {
    const from = s.slice(ch[0].a.start, ch[ch.length - 1].b.end).replace(/\s+/g, ' ')
    const to = ch[0].a.text + ch.map((j) => j.b.text).join('')
    return `${from} → ${to}`
  })
  // A join with no leading-zero proof got here on the "this comma is not in an argument list" proof,
  // so the hint that USED to be attached ("space it out if you meant two arguments") is now false —
  // there is no argument reading at that position at all. Say the true thing instead.
  const outsideCall = joins.some((j) => !j.zeroRun)

  // Right-to-left so every offset stays valid while the gaps are deleted.
  let text = s
  for (let k = joins.length - 1; k >= 0; k -= 1) {
    text = text.slice(0, joins[k].a.end) + text.slice(joins[k].b.start)
  }
  return {
    text,
    repair: {
      code: 'THOUSANDS_SEP',
      message: `ตัดจุลภาคคั่นหลักพันในตัวเลขออก (${shown.join(', ')}) — จุลภาคในสูตรคือตัวคั่นอาร์กิวเมนต์`
        + (outsideCall ? ' (จุลภาคตรงนี้ไม่ได้อยู่ในวงเล็บของฟังก์ชัน จึงเป็นตัวคั่นอาร์กิวเมนต์ไม่ได้ อ่านเป็นตัวเลขหลักพันได้อย่างเดียว)' : ''),
    },
    problems,
  }
}

// HTML entities: copying the spec out of a rendered page is exactly how these circulate.
const HTML_ENTITIES = [
  [/&gt;/gi, '>'], [/&lt;/gi, '<'], [/&quot;/gi, '"'], [/&#0*39;/g, "'"], [/&apos;/gi, "'"],
  [/&nbsp;/gi, ' '], [/&amp;/gi, '&'], // &amp; LAST — decoding it first would revive &amp;gt; wrongly
]

/**
 * Strip `// …` and `# …` line comments plus `/* … *\/` block comments — quote-aware, so a literal
 * reading `'a//b'` or `'PND/90'` survives untouched.
 *
 * The blueprint annotates almost every formula line this way (`… * 0.30)   // (ก) บ้าน 30%`), and
 * neither `//` nor `#` is ever an operator here: `//` is not division-division in any expression a
 * human writes, and `#` is not a token at all. A block comment can sit INSIDE an argument list
 * (`Min(ded_political /* พรรค *\/, 10000)`), which is why line-based stripping alone is not enough.
 * @returns {{text:string, line:boolean, block:boolean}}
 */
function stripComments(s) {
  let out = ''
  let i = 0
  let line = false
  let block = false
  while (i < s.length) {
    const c = s[i]
    if (c === "'") {
      const close = s.indexOf("'", i + 1)
      if (close === -1) { out += s.slice(i); break }
      out += s.slice(i, close + 1)
      i = close + 1
      continue
    }
    if (c === '/' && s[i + 1] === '*') {
      const end = s.indexOf('*/', i + 2)
      block = true
      if (end === -1) { i = s.length; continue } // unterminated — the rest is comment
      i = end + 2
      continue
    }
    if ((c === '/' && s[i + 1] === '/') || c === '#') {
      const nl = s.indexOf('\n', i)
      line = true
      if (nl === -1) break
      i = nl // keep the newline itself — the line-count checks below still need it
      continue
    }
    out += c
    i += 1
  }
  return { text: out, line, block }
}

/**
 * The contents of the FIRST markdown code fence, or null when there is none.
 *
 * When a fence is present its contents are AUTHORITATIVE and everything outside it is discarded: an
 * LLM answer is "sentence, fence, explanation", and the old repair — deleting only the ``` marker
 * LINES — left the sentences behind, so pasting a whole reply (literally the stated use case) died
 * with a character offset pointing at a Thai or English word.
 * @returns {{inner:string, outside:boolean, extra:number}|null}
 */
function extractFence(s) {
  const re = /^[ \t]*```[^\n]*\n([\s\S]*?)^[ \t]*```[ \t]*$/gm
  const blocks = []
  let m
  while ((m = re.exec(s)) !== null) blocks.push({ inner: m[1], start: m.index, end: re.lastIndex })
  if (!blocks.length) {
    // An UNCLOSED fence (the paste was truncated, or the closing marker was left out) still tells us
    // where the formula starts — take everything after the opening marker rather than choking on it.
    const open = s.match(/^[ \t]*```[^\n]*\n([\s\S]*)$/m)
    if (open) return { inner: open[1], outside: open.index > 0, extra: 0 }
    return null
  }
  const first = blocks[0]
  const outside = s.slice(0, first.start).trim() !== '' || s.slice(first.end).trim() !== ''
  return { inner: first.inner, outside, extra: blocks.length - 1 }
}

// ──────────────────────────────────────────────────────────────────────────────
//  Unrepairable constructs → one Thai sentence each, with the offending fragment
// ──────────────────────────────────────────────────────────────────────────────

/** A short window of `text` around `index`, for quoting the offending fragment back at the author. */
function snippet(text, index, span = 16) {
  const from = Math.max(0, index - span)
  const to = Math.min(text.length, index + span)
  return (from > 0 ? '…' : '') + text.slice(from, to).trim() + (to < text.length ? '…' : '')
}

/**
 * Everything the repairs cannot fix, found on the MASKED text so a literal never triggers one.
 * Ordered most-specific first; each message names the construct AND the way to write it instead.
 * @param {string} expr  the fully-repaired expression
 * @returns {Array<{code:string, message:string}>}
 */
function diagnose(expr) {
  const problems = []
  const masked = maskLiterals(expr)
  const add = (code, message) => problems.push({ code, message })

  // SQL CASE. Every keyword is a legal identifier, so without this the author reads
  // "มีข้อความเกินหลังจบสูตร: WHEN" — a typo report for what is really a grammar limit.
  const caseWhen = masked.match(/\bCASE\b[\s\S]*?\bWHEN\b/i)
  if (caseWhen) {
    add('CASE_WHEN', `ไม่รองรับ CASE WHEN … THEN … END (ที่ ${snippet(masked, caseWhen.index)}) — `
      + 'ใช้ IF(เงื่อนไข, ค่าเมื่อจริง, ค่าเมื่อเท็จ) แทน เช่น IF(has_spouse = 1, 60000, 0) และซ้อน IF สำหรับหลายกรณี')
  }
  // A JavaScript arrow function. `=>` is not a token here at all, and the `=` + `>` it is built from
  // is what used to make the FIRST line the author read be a lecture about chained comparisons — a
  // diagnosis of a symptom of a symptom. What they actually pasted is code, and that is the headline.
  const arrow = masked.indexOf('=>')
  if (arrow !== -1) {
    add('ARROW_FN', `นี่เป็นโค้ด JavaScript ไม่ใช่ตัวสูตร (เจอ "=>" ที่ ${snippet(masked, arrow)}) — `
      + 'ให้วางเฉพาะตัวนิพจน์ ไม่ต้องมี const/function/=>/return และให้อ้างชื่อตัวแปรในระบบแทนพารามิเตอร์ '
      + 'เช่น IF(use_actual_40_7 = 1, exp_40_7_actual, inc_40_7 * 0.60)')
  }
  // Boolean operators. There is no boolean type in this grammar at all — `IF` nests instead.
  const andOr = masked.match(/&&|\|\||\band\b|\bor\b/i)
  if (andOr) {
    add('BOOL_OP', `ไม่รองรับตัวดำเนินการทางตรรกะ "${andOr[0]}" (ที่ ${snippet(masked, andOr.index)}) — `
      + 'ไวยากรณ์นี้ไม่มี AND/OR ให้ซ้อน IF แทน เช่น IF(a > 1, IF(b > 2, x, y), y)')
  }
  // Chained comparison. `parseComparison` takes exactly ONE comparator, so `a < b < c` dies as a
  // trailing token — again a typo report where the truth is "this grammar has no AND to expand into".
  // The middle must be at least one character and may contain NO comparator character (so the two
  // halves of `==` cannot be read as two comparators) and no ( ) , (so `IF(a > 1, 1, 0) > 2` — two
  // comparators in two different comparisons — cannot match either).
  //
  // SUPPRESSED when `&&`/`and`/`=>` is present: `a > 1 && b > 2` matches the pattern only BECAUSE of
  // the operator already reported above, so leading with "เปรียบเทียบต่อกันไม่ได้" points the author at
  // a construct they did not write. The primary cause goes first, and the symptom is not repeated.
  const chain = (andOr || arrow !== -1) ? null
    : masked.match(/(<=|>=|==|!=|<>|[<>=])[^(),<>=!]+(<=|>=|==|!=|<>|[<>=])/)
  if (chain) {
    add('CHAINED_COMPARE', `เปรียบเทียบต่อกันไม่ได้ (ที่ ${snippet(masked, chain.index)}) — `
      + 'เขียนได้ครั้งละหนึ่งการเปรียบเทียบ ถ้าต้องการสองเงื่อนไขให้ซ้อน IF เช่น IF(x > 150000, IF(x < 300000, 1, 0), 0)')
  }
  const notWord = masked.match(/\bnot\b/i)
  if (notWord) {
    add('BOOL_NOT', `ไม่รองรับ NOT (ที่ ${snippet(masked, notWord.index)}) — `
      + 'ให้กลับด้านเงื่อนไขเอง เช่น a != 1 หรือสลับ THEN/ELSE ของ IF')
  }
  const bang = masked.search(/!(?!=)/)
  if (bang !== -1) {
    add('BANG', `ไม่รองรับเครื่องหมาย "!" (ที่ ${snippet(masked, bang)}) — `
      + 'มีแต่ != (ไม่เท่ากับ) เขียนเงื่อนไขตรง ๆ แทน เช่น has_spouse == 0')
  }
  // Ternary. `?` is not a token at all, and `:` only ever shows up as its partner.
  const tern = masked.indexOf('?')
  if (tern !== -1) {
    add('TERNARY', `ไม่รองรับ ternary "? :" (ที่ ${snippet(masked, tern)}) — ใช้ IF(เงื่อนไข, ค่าเมื่อจริง, ค่าเมื่อเท็จ)`)
  } else if (masked.indexOf(':') !== -1) {
    add('COLON', `ไม่รองรับเครื่องหมาย ":" (ที่ ${snippet(masked, masked.indexOf(':'))})`)
  }
  const pct = masked.indexOf('%')
  if (pct !== -1) {
    add('PERCENT', `ไม่รองรับ "%" (ที่ ${snippet(masked, pct)}) — เขียนเป็นการคูณ/หารแทน เช่น 30% → * 0.30`)
  }
  const pow = masked.indexOf('^')
  if (pow !== -1) {
    add('POWER', `ไม่รองรับยกกำลัง "^" (ที่ ${snippet(masked, pow)}) — ให้คูณซ้ำแทน`)
  }
  // `**` is Python/JS exponentiation. It tokenizes as MUL MUL, so the raw error is an operator
  // sequence that names neither the construct nor the reason.
  const pow2 = masked.indexOf('**')
  if (pow2 !== -1) {
    add('POWER', `ไม่รองรับยกกำลัง "**" (ที่ ${snippet(masked, pow2)}) — ให้คูณซ้ำแทน เช่น x * x`)
  }
  // ── `.` and `[ ]` — ACCEPTED now, but only in the one shape that is a NAME ────────────────────
  // Both used to be blanket refusals, and both are now the spelling of a nested PATH
  // (`INCOME_40_5.subItems[].items[].amount`). What must stay refused is everything that would make a
  // path an EXPRESSION: a computed index, a method call, a dangling or doubled separator. Those all
  // have exact tokenizer messages (see describeParseError), so the job here is only to catch the two
  // shapes whose raw message would still point at the wrong thing.
  //
  // A path used as a CALLEE. `a.b()` tokenizes as one path followed by `(`, so the raw report is a
  // trailing-token error naming a paren — which says nothing about the method call that was written.
  const pathCall = masked.match(/[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*|\[\])+\s*\(/)
  if (pathCall) {
    add('PATH_CALL', `ไม่รองรับการเรียกเมธอดบนเส้นทางข้อมูล "${pathCall[0].trim()}" (ที่ ${snippet(masked, pathCall.index)}) — `
      + 'เส้นทางข้อมูลเป็น "ชื่อ" ไม่ใช่ฟังก์ชัน เช่น INCOME_40_5.subItems[].amount')
  }
  // A bracket that is not the EMPTY pair sitting directly after a segment name. `[NAME]` was already
  // rewritten above (the blueprint's notation), so anything left here is `items[0]`, `items[i]`, a lone
  // `]`, or `[]` with nothing in front of it.
  for (let i = 0; i < masked.length; i += 1) {
    if (masked[i] === '[') {
      const emptyPair = masked[i + 1] === ']'
      const afterName = i > 0 && /[A-Za-z0-9_]/.test(masked[i - 1])
      if (emptyPair && afterName) { i += 1; continue }
      add('BRACKET', `วงเล็บเหลี่ยมใช้ได้แบบเดียวคือ [] ที่ต่อท้ายชื่อชั้นในเส้นทางข้อมูล (ที่ ${snippet(masked, i)}) — `
        + 'เช่น subItems[] ที่แปลว่า "ทุกรายการ" — ระบุลำดับรายการอย่าง items[0] ไม่ได้')
      break
    }
    if (masked[i] === ']') {
      add('BRACKET', `เจอ "]" ที่ไม่มี "[" คู่กัน (ที่ ${snippet(masked, i)}) — วงเล็บเหลี่ยมใช้ได้แบบเดียวคือ [] ต่อท้ายชื่อชั้น เช่น subItems[]`)
      break
    }
  }
  // A UNIT written after a number (`9000 THB`, `240000 บาท`). It is NOT stripped: `THB` is spelled
  // exactly like a variable, and deleting a name the author typed is the silent rewrite rule 1
  // forbids. But the raw parser message for it is `ติดตรง "THB"`, which never says the one thing that
  // fixes it — drop the unit. A number can never be followed by a name, so this shape is always wrong.
  const unit = masked.match(/(\d+(?:\.\d+)?)\s*(THB|BAHT|USD|EUR|JPY)\b/i) || masked.match(/(\d+(?:\.\d+)?)\s*(บาท)/)
  if (unit) {
    add('UNIT_SUFFIX', `ไม่ต้องใส่หน่วยเงินในสูตร (เจอ "${unit[2]}" ที่ ${snippet(masked, unit.index)}) — `
      + `ใส่เฉพาะตัวเลข เช่น ${unit[1]} (ถ้า ${unit[2]} เป็นชื่อตัวแปรจริง ต้องมีเครื่องหมายคั่น เช่น ${unit[1]} * ${unit[2]})`)
  }
  // `60 percent` — same shape, different fix: the rate has to become a decimal. Guessing 0.60 for the
  // author is exactly the "wrong answer that looks right" this module refuses to produce.
  const pctWord = masked.match(/(\d+(?:\.\d+)?)\s*(percent|pct)\b/i) || masked.match(/(\d+(?:\.\d+)?)\s*(เปอร์เซ็นต์)/)
  if (pctWord) {
    add('PERCENT_WORD', `เขียนอัตราเป็นทศนิยมแทนคำว่า "${pctWord[2]}" (ที่ ${snippet(masked, pctWord.index)}) — `
      + 'เช่น 60 percent → * 0.60 (ระบบไม่เดาค่าให้ เพราะ percent อาจเป็นชื่อตัวแปรก็ได้)')
  }
  // `'FEM''ALE'` — a doubled quote, i.e. the SQL escape, in a grammar that has no escape at all. The
  // segmenter reads it as two literals back to back and the parser then reports `ติดตรง "ALE"`, an
  // artifact of quote pairing that points nowhere near the problem the author needs to see.
  const segs = splitLiterals(expr)
  for (let i = 1; i < segs.length - 1; i += 1) {
    if (!segs[i].lit || !segs[i + 2] || !segs[i + 2].lit) continue
    if (segs[i + 1].lit || segs[i + 1].text.trim() !== '') continue
    add('ADJACENT_LITERAL', `ข้อความในเครื่องหมายคำพูดสองชุดติดกัน (${segs[i].text} กับ ${segs[i + 2].text}) — `
      + "ไวยากรณ์นี้ไม่มีวิธี escape เครื่องหมาย ' ในข้อความ ให้เขียนเป็นข้อความเดียว "
      + `เช่น '${segs[i].text.slice(1, -1)}${segs[i + 2].text.slice(1, -1)}'`)
    break
  }
  // (The numeric comma — `100,000` vs `0,30` vs an ordinary argument separator — is decided in
  // `repairNumericCommas`, positionally and BEFORE this point. It cannot be a text pattern here: the
  // old `/\d,\d/` scan over the whole expression refused `MIN(1,2)` and `IF(has_spouse=1,60000,0)`,
  // i.e. the commonest correct spelling there is, with a message accusing a perfectly good number.)

  // Numbers the PERSISTED text cannot hold. `String(1e21)` / `String(1e-7)` are exponent notation,
  // which this project's tokenizer cannot read back: the formula would save fine and then be
  // unopenable the first time the stored graph is absent or drifted (bundle import, version clone).
  NUMBER_TOKEN.lastIndex = 0
  let nm
  const badNums = new Set()
  while ((nm = NUMBER_TOKEN.exec(masked)) !== null) {
    const v = Number(nm[0])
    if (!Number.isFinite(v) || badNums.has(nm[0])) continue
    if (String(v).indexOf('e') !== -1 || Math.abs(v) >= 1e15) {
      badNums.add(nm[0])
      add('NUMBER_RANGE', `ตัวเลข ${nm[0]} เก็บเป็นข้อความสูตรไม่ได้ (ระบบจะเขียนเป็น ${String(v)} ซึ่งอ่านกลับไม่ได้) — `
        + 'ใช้ตัวเลขไม่เกิน 15 หลัก และไม่เล็กกว่า 0.000001 ถ้าจำเป็นให้คูณ/หารด้วยตัวคูณแทน')
    }
  }

  // Parentheses — WHERE, not just "unbalanced". An admin cannot count 40 characters of nesting.
  let depth = 0
  for (let i = 0; i < masked.length; i += 1) {
    if (masked[i] === '(') depth += 1
    else if (masked[i] === ')') {
      depth -= 1
      if (depth < 0) {
        add('PAREN_EXTRA_CLOSE', `วงเล็บปิดเกินมา 1 ตัว ที่ตำแหน่ง ${i + 1} (${snippet(masked, i)})`)
        break
      }
    }
  }
  if (depth > 0) {
    add('PAREN_UNCLOSED', `วงเล็บเปิดค้างอยู่ ${depth} ตัว — ตรวจปลายสูตร (${snippet(masked, masked.length - 1)})`)
  }

  // Retired + unknown functions, and retired identifiers: an identifier, and whether a '(' follows it
  // (offsets are intact — masking pads with spaces).
  //
  // RETIRED is reported SEPARATELY from UNKNOWN, and that separation is the point. `SUMITEMS(…)` and
  // `item_amount` are not typos and not inventions: they are what this editor told authors to write
  // until the `[]` path replaced them, so a stored formula, a saved LLM prompt and an admin's notes
  // all still contain them. "ไม่รู้จักฟังก์ชัน" would be true and useless. The migration sentence names
  // the path to write instead — the same sentence the backend evaluator answers with, so the two
  // cannot tell an author two different stories.
  //
  // Diagnosed HERE, on the masked text, rather than left to the parser (which throws the same
  // sentences), for one reason: this runs BEFORE `parseExpression`, so a paste that is retired AND
  // malformed still leads with the retirement rather than with a syntax complaint about a construct
  // that no longer exists.
  IDENT_G.lastIndex = 0
  let m
  const reported = new Set()
  while ((m = IDENT_G.exec(masked)) !== null) {
    let j = m.index + m[0].length
    while (j < masked.length && /\s/.test(masked[j])) j += 1
    const isCall = masked[j] === '('
    const name = m[0]
    if (isCall) {
      const fn = name.toUpperCase()
      if (reported.has(fn)) continue
      if (isRetiredFunction(fn)) {
        reported.add(fn)
        add('RETIRED_FN', retiredFunctionMessage(fn))
        continue
      }
      if (ALLOWED_FN_SET.has(fn)) continue
      reported.add(fn)
      add('UNKNOWN_FN', `ไม่รู้จักฟังก์ชัน ${name}() (ที่ ${snippet(masked, m.index)}) — รองรับเฉพาะ ${FN_LIST_TH}`)
      continue
    }
    // A retired identifier is only retired as a NAME. `INCOME_40_5.subItems[].item_amount` is a path
    // segment that happens to be spelled the same, and a path is masked… no — it is NOT masked here,
    // so the preceding character is what tells the two apart: a segment always follows a '.'.
    const key = name.toLowerCase()
    if (!isRetiredIdentifier(key) || reported.has(key)) continue
    if (m.index > 0 && masked[m.index - 1] === '.') continue
    reported.add(key)
    add('RETIRED_IDENT', retiredIdentifierMessage(key))
  }

  return problems
}

/** Re-word a raw `parseExpression` throw into something an admin can act on. */
function describeParseError(err, expr) {
  const raw = (err && err.message) ? String(err.message) : 'parse error'
  const at = raw.match(/unexpected character "(.)" at (\d+)/)
  if (at) {
    const idx = Number(at[2])
    return `มีอักขระที่ไวยากรณ์ไม่รู้จัก "${at[1]}" ที่ตำแหน่ง ${idx + 1} (${snippet(expr, idx)})`
  }
  // `unexpected token` is thrown by parsePrimary when an OPERAND was required and something else
  // arrived. Without this branch the dialog — Thai everywhere else — prints the raw English
  // `parse error: unexpected token ">"`, which is the one message a non-developer cannot act on.
  const tok = raw.match(/unexpected token "([^"]*)"/)
  if (tok) {
    const t = tok[1]
    const hint = {
      '=': ' — เครื่องหมาย = ต้องมีตัวแปรหรือตัวเลขอยู่ทั้งสองข้าง (เท่ากับให้ใช้ = หรือ ==)',
      '>': ' — เครื่องหมายเปรียบเทียบต้องมีค่าอยู่ทั้งสองข้าง',
      '<': ' — เครื่องหมายเปรียบเทียบต้องมีค่าอยู่ทั้งสองข้าง',
      '*': ' — ตัวดำเนินการต้องมีค่าอยู่ทั้งสองข้าง',
      '/': ' — ตัวดำเนินการต้องมีค่าอยู่ทั้งสองข้าง',
      ')': ' — วงเล็บปิดโดยยังไม่มีค่าอยู่ข้างใน',
      ',': ' — มีจุลภาคติดกันหรือค้างอยู่โดยไม่มีค่าตามหลัง',
    }[t] || ''
    return `เจอ "${t}" ในตำแหน่งที่ต้องเป็นตัวเลข ตัวแปร หรือวงเล็บเปิด${hint}`
  }
  // The literal charset is deliberately narrow (formulaGraph `isSafeStringLiteral`) — quoting the
  // literal back is the whole message here: what is illegal in it is usually invisible in a diff.
  const badLit = raw.match(/invalid string literal '([^']*)'/)
  if (badLit) {
    return `ข้อความ '${badLit[1]}' มีอักขระที่ไม่อนุญาต — ใช้ได้เฉพาะตัวอักษร ตัวเลข ช่องว่าง และ _ - . / (ยาวไม่เกิน 100 ตัว)`
  }
  if (/unterminated string literal/.test(raw)) {
    return "เครื่องหมาย ' เปิดไว้แล้วไม่ได้ปิด — ข้อความต้องอยู่ในเครื่องหมาย ' … ' ครบคู่"
  }
  if (/cannot be nested/.test(raw)) {
    return `${SUM_FN}() ซ้อนกันไม่ได้ — ${SUM_FN} ชั้นในรวมรายการเป็นค่าเดียวไปแล้ว จึงไม่เหลืออะไรให้ชั้นนอกรวมอีก`
  }
  if (new RegExp(`${SUM_FN}\\(\\) needs exactly 1 arg`).test(raw)) {
    return `${SUM_FN}() รับได้ 1 อาร์กิวเมนต์เท่านั้น — เขียน ${SUM_FN}(<สูตรต่อรายการ>) `
      + `ถ้าต้องรวมสองชุดให้เขียนแยกเป็น ${SUM_FN}(…) + ${SUM_FN}(…)`
  }
  if (/index inside \[ \] is not allowed/.test(raw)) {
    return 'ระบุลำดับรายการในวงเล็บเหลี่ยมไม่ได้ — ใช้ [] เปล่า ๆ ที่แปลว่า "ทุกรายการในชั้นนั้น" เช่น subItems[].amount'
  }
  if (/repeated \[\]\[\]/.test(raw)) {
    return 'ใส่ [] ซ้อนกันสองชุดในชั้นเดียวไม่ได้ — หนึ่งชั้นมี [] ได้ชุดเดียว เช่น subItems[].items[].amount'
  }
  if (/a path is a name, not a function/.test(raw)) {
    const nm = raw.match(/"([^"]*)\("/)
    return `เรียกเมธอดบนเส้นทางข้อมูลไม่ได้${nm ? ` ("${nm[1]}")` : ''} — เส้นทางข้อมูลเป็นชื่อ เช่น INCOME_40_5.subItems[].amount`
  }
  if (/\[ \] is only legal as the empty pair/.test(raw)) {
    return 'วงเล็บเหลี่ยมใช้ได้แบบเดียวคือ [] ที่ต่อท้ายชื่อชั้นในเส้นทางข้อมูล เช่น subItems[] — เขียนนำหน้าหรือลอย ๆ ไม่ได้'
  }
  if (/needs an EVEN number of args/.test(raw)) {
    return `${IFS_FN}() ต้องใส่เป็นคู่ "เงื่อนไข, ค่า" เสมอ และปิดท้ายด้วย TRUE, ค่าเริ่มต้น `
      + `เช่น ${IFS_FN}(rate_code == 'BUILDING_30', 0.3, TRUE, 0)`
  }
  const arity = raw.match(/(MIN|MAX|ROUND|IF)\(\) (needs [^"]*)/)
  if (arity) {
    const th = { 'needs ≥2 args': 'ต้องมีอย่างน้อย 2 ค่า', 'needs 1–2 args': 'ต้องมี 1–2 ค่า', 'needs exactly 3 args': 'ต้องมี 3 ค่า (เงื่อนไข, ค่าเมื่อจริง, ค่าเมื่อเท็จ)' }
    return `${arity[1]}() ${th[arity[2].trim()] || arity[2]}`
  }
  const near = raw.match(/near "([^"]*)"/)
  if (near) return `สูตรไม่สมบูรณ์ — ติดตรง "${near[1] === '<eof>' ? 'ท้ายสูตร' : near[1]}"`
  if (/unexpected end of expression/.test(raw)) return 'สูตรขาดตอน — ตรวจว่ามีตัวเลข/ตัวแปรครบทุกช่อง'
  if (/trailing token/.test(raw)) {
    const t = raw.match(/trailing token "([^"]*)"/)
    return `มีข้อความเกินหลังจบสูตร: "${t ? t[1] : ''}" — หนึ่งช่องแก้ไขเก็บได้สูตรเดียว`
  }
  return raw
}

/**
 * WHY a canonical could not be read back — named, not guessed at.
 *
 * The old message blamed "ตัวเลขที่ใหญ่หรือเล็กเกินไป" unconditionally, which is one of two causes and
 * not the one that actually fires: every observed trigger is a comparison whose OPERAND is another
 * comparison (`(a = b) = c`, `IF((a < b) < c, 1, 0)`). The generator writes no parentheses around a
 * comparison, so `(a = b) = c` comes back as `a == b == c`, which re-parses as something else — and an
 * author told to "adjust the number" in a formula containing no large number learns nothing.
 * @param {string} canonical  the unreadable canonical
 */
function describeUnstableCause(canonical) {
  const masked = maskLiterals(canonical)
  if (/(<=|>=|==|!=|<>|[<>=])[^(),<>=!]+(<=|>=|==|!=|<>|[<>=])/.test(masked)) {
    return 'สาเหตุคือมีการเปรียบเทียบซ้อนอยู่ในการเปรียบเทียบ (เช่น (a = b) = c) ซึ่งเขียนเป็นข้อความแล้ว'
      + 'วงเล็บจะหายไปจนอ่านกลับเป็นคนละความหมาย — ถ้าต้องการใช้ผลการเปรียบเทียบเป็นค่า ให้ห่อด้วย IF ก่อน '
      + 'เช่น IF(IF(a = b, 1, 0) = c, 1, 0)'
  }
  if (/\d[eE][+-]?\d/.test(masked)) {
    return 'สาเหตุคือตัวเลขใหญ่หรือเล็กเกินไปจนถูกเขียนเป็นเลขยกกำลังสิบ ให้ใช้ตัวเลขไม่เกิน 15 หลัก '
      + 'และไม่เล็กกว่า 0.000001'
  }
  return 'ให้ลดความซ้อนของวงเล็บ/การเปรียบเทียบ หรือปรับตัวเลขแล้วลองใหม่'
}

// ──────────────────────────────────────────────────────────────────────────────
//  Assignment head · prose extraction
// ──────────────────────────────────────────────────────────────────────────────

/** Does `s` parse on its own? (Grammar only — see `isFormula` for the stronger test.) */
function parses(s) {
  try { parseExpression(s); return true } catch (_err) { return false }
}

/**
 * Is `s` a whole FORMULA — not merely something the grammar can parse?
 *
 * The difference matters exactly once, and it is a silent-wrong-answer difference: `sex = 'FEMALE'`
 * is a legal comparison, and its right-hand side `'FEMALE'` PARSES. Reading the `sex =` as an
 * assignment head therefore looked safe and quietly turned a comparison into a bare string — which
 * the anchor then refuses ("สูตรต้องคืนค่าเป็นตัวเลข") only AFTER the canvas has been rebuilt.
 * Compiling through the real anchor here is what makes the two answers agree.
 */
function isFormula(s) {
  if (!parses(s)) return false
  const c = canonicalizeThroughAnchor(s)
  return c.errors.length === 0 && c.canonical !== ''
}

/**
 * Is `s` a COMPUTATION rather than a bare value? The same judgement the prose gate makes, for the same
 * reason (`proseTrimRefusal` → `CORE_IS_COMPUTATION`), reused rather than re-derived — the two must not
 * drift apart. See `stripAssignmentHead` for why a bare value is the one shape that cannot be decided.
 */
function isComputation(s) {
  return CORE_IS_COMPUTATION.test(maskLiterals(s))
}

/**
 * Strip ONE leading `name = …` assignment head, in any of the spellings an LLM reaches for.
 *
 * The blueprint writes every formula that way, so this repair is the difference between "paste works"
 * and "paste never works". It is also AMBIGUOUS for the bare `=`, which is a legal EQ comparator
 * here: `sex = 'FEMALE'` is a whole formula, not an assignment to `sex`.
 *
 * Resolution, in order:
 *   a) the separator is `:=` / `->` / a non-ASCII (Thai) label → unambiguous, there is no reading in
 *      which those are an operator of this grammar;
 *   b) the name IS this formula's รหัสสูตร → unambiguously an assignment;
 *   c) the remainder is a COMPUTATION that compiles as a formula (`MIN(a, 100000)`, `x * 0.3`) → strip,
 *      and say so loudly, including how to get the comparison back (`==`), because we may have guessed
 *      against the author. A comparison is never written with a bare computation on the right when the
 *      left is a single name and the whole thing is the entire paste, so this reading is safe;
 *   d) the remainder is a BARE VALUE that would compile (`sex = 1`, `expense_40_4 = 0`) → REFUSE.
 *      This is the one shape with two ordinary readings and nothing to separate them: `expense_40_4 = 0`
 *      is the blueprint declaring a formula, `sex = 1` is a comparison — identical text, opposite
 *      meanings. It used to strip unconditionally, so the comparison silently became its own right
 *      operand (`sex = 1` → the constant `1`, ok:true, green preview). The author is asked instead;
 *      in the editor the ambiguity usually never arises, because (b) resolves it from the รหัสสูตร;
 *   e) the remainder does not compile as a formula at all (`sex = 'FEMALE'` — a string is not a formula
 *      result) → there is only one valid reading left, so it is NOT a head: leave the comparison alone.
 * The name is REPORTED, never applied: overwriting the author's รหัสสูตร from pasted text is exactly
 * the kind of silent rewrite rule 1 exists to forbid.
 * @returns {{text:string, name:?string, repair:?{code:string,message:string},
 *            problem:?{code:string,message:string}}}
 */
function stripAssignmentHead(text, selfName) {
  //  ①  **bold** wrapper — markdown emphasis leaks out of chat UIs constantly. Only stripped when it
  //     wraps the head (i.e. an `=` follows), never as a general rule: `**` is also the Python power
  //     operator, and those two must not share a repair.
  const bold = text.match(/^\s*\*\*\s*([^*\n]{1,64}?)\s*\*\*\s*(?=(?::=|->|=(?!=)))/)
  const boldName = bold ? bold[1] : null
  const head = bold ? text.slice(bold[0].length) : text

  //  ②  the separator. `:=` and `->` are pseudocode/arrow assignment; neither exists in this grammar,
  //     so finding one is proof of intent (`->` even parses today, as SUB followed by GT — a garbage
  //     comparison, the same silent family as the bare `=`). With ① already consumed, the name is
  //     gone and only the separator is left to match.
  const m = bold
    ? head.match(/^\s*(:=|->|=(?!=))\s*/)
    : head.match(/^\s*([A-Za-z_][A-Za-z0-9_]*|[^\s=:<>!()'"[\]]{1,64})\s*(:=|->|=(?!=))\s*/)
  const none = { text, name: null, repair: null, problem: null }
  if (!m) return none

  const name = boldName != null ? boldName : m[1]
  const sep = bold ? m[1] : m[2]
  const rest = head.slice(m[0].length)
  if (rest === '') return none

  const asciiName = /^[A-Za-z_][A-Za-z0-9_]*$/.test(name)
  // A non-ASCII label (`เงินได้สุทธิ =`) cannot be anything BUT a label: Thai is illegal everywhere in
  // this grammar except inside a literal, and a literal cannot sit unquoted at the head of a line.
  const labelName = /[^\x20-\x7E]/.test(name)
  // …and anything that is NEITHER (`100 =`, `x.y =`) is not a head at all — reading one there would
  // silently delete the left side of a real comparison.
  if (!asciiName && !labelName) return none
  const unambiguous = sep !== '=' || labelName
  const isSelf = !!selfName && asciiName && selfName.toLowerCase() === name.toLowerCase()

  if (!unambiguous && !isSelf) {
    // (e) the right side is not a formula at all → the comparison is the only valid reading. Leave it.
    if (!isFormula(rest)) return none
    // (d) …but a bare VALUE on the right compiles just as happily as a computation does, and THAT is
    //     where the two readings become indistinguishable. Ask; never pick one silently.
    if (!isComputation(rest)) {
      return {
        text,
        name: null,
        repair: null,
        problem: {
          code: 'ASSIGN_OR_COMPARE',
          message: `"${name} ${sep} ${rest}" อ่านได้ 2 แบบ และระบบไม่เดาให้ — `
            + `ถ้า "${name}" คือรหัสสูตรที่กำลังแก้ ให้วางเฉพาะฝั่งขวา (${rest}) · `
            + `ถ้าตั้งใจจะเปรียบเทียบ ให้เขียน ${name} == ${rest}`,
        },
      }
    }
  }

  const message = isSelf
    ? `ตัด "${name} ${sep}" ออก (ตรงกับรหัสสูตรปัจจุบัน — เก็บเฉพาะฝั่งขวาไว้เป็นสูตร)`
    : `ตัด "${name} ${sep}" ออก แล้วใช้เฉพาะฝั่งขวาเป็นสูตร${sep === '=' ? ` — ถ้าตั้งใจจะ "เปรียบเทียบ" ให้เขียน ${name} == …` : ''}`
  return { text: rest, name: asciiName ? name : null, repair: { code: 'ASSIGNMENT', message }, problem: null }
}

/**
 * The names assigned to at the TOP LEVEL of the paste — the real signal for "this is several
 * formulas", replacing the old "any line starting `IDENT =`".
 *
 * That old rule manufactured its own false positive: the bracket repair turns a pretty-printed
 * `    [USE_ACTUAL_40_5A] = 1,` into a line-leading `USE_ACTUAL_40_5A =`, so the blueprint's own
 * formula, wrapped across lines the way an LLM formats it, was refused as "2 สูตร" — and the message
 * named a VARIABLE as a formula. An assignment head can only be at paren depth 0; anything deeper is
 * an argument, i.e. an EQ comparison.
 * @returns {string[]}
 */
function topLevelAssignmentHeads(text) {
  const masked = maskLiterals(text)
  const heads = []
  let depth = 0
  masked.split('\n').forEach((line) => {
    const m = line.match(LINE_ASSIGN_RE)
    if (m && depth === 0) heads.push(m[1])
    for (let i = 0; i < line.length; i += 1) {
      if (line[i] === '(') depth += 1
      else if (line[i] === ')') depth -= 1
    }
  })
  return heads
}

// A word that can be PROSE: it carries no character that only ever appears in a formula. Trimming is
// allowed to eat these at the two ends and nothing else, which is what keeps extraction honest —
// `</script>` or `+ 5000` can never be mistaken for a sentence and thrown away.
const PROSE_WORD = /^[^()'"[\]<>=+*/&|?!%^`$\\{}~]+$/
const MAX_TRIM_WORDS = 40

// A token in the DISCARDED text that cannot be dismissed as a natural-language word: a number, or a
// code-shaped name (anything carrying `_`, which is how every variable in this catalog is spelled).
// Finding one means the trim would be throwing away something that may be part of the FORMULA.
const DROPPED_NOT_PROSE = /\d+(?:\.\d+)?|[A-Za-z0-9]*_[A-Za-z0-9_]*/
// A standalone operator among the discarded words (`… 30000 บาท - ค่าใช้จ่าย`). Only `-` can reach
// here — PROSE_WORD already rejects `+ * /` — and only as a whole word, so `mortgage-interest` (a real
// English word in a real corpus case) is still ordinary prose.
const DROPPED_OPERATOR = /(?:^|\s)[-+*/](?:\s|$)/
// A core that is a single value — a bare number or a bare name — is not a formula anyone WROTE; it is
// whatever fragment happened to parse after the sentence around it was deleted.
const CORE_IS_COMPUTATION = /[()+\-*/<>=]/

/**
 * Why a candidate trim must NOT be taken, or null when it is safe.
 *
 * This is the gate that separates "trimmed a sentence off a formula" from "invented a formula out of a
 * sentence", and it is the difference between a feature and a hazard. `parses(core)` alone is nowhere
 * near enough, because a BARE NUMBER and a BARE IDENTIFIER both parse:
 *
 *   ถ้า has_spouse เท่ากับ 1 ให้ 60000 ไม่งั้น 0   → core `0`                — a silent ZERO, from an
 *                                                                              instruction that is not
 *                                                                              a formula at all
 *   ให้ใช้ inc_40_3 * 0.5 แต่ไม่เกิน 100000        → core `inc_40_3 * 0.5`   — the CAP silently dropped,
 *                                                                              i.e. wrong in the
 *                                                                              direction of under-taxing
 *
 * Both are caught by one idea: trimming is only honest when what is thrown away is GENUINELY natural
 * language. If the discarded text still carries a number, a `_`-shaped name or a bare operator, then it
 * may have been part of the calculation and we must not guess — and if what SURVIVES is a lone value
 * rather than a computation, there was no formula in the paste to begin with.
 */
function proseTrimRefusal(core, dropped) {
  const stray = dropped.match(DROPPED_NOT_PROSE)
  if (stray) {
    return `ไม่พบสูตรที่แน่ใจได้ในข้อความที่วาง — ถ้าตัดคำอธิบายออกจะเหลือ "${core}" แต่ส่วนที่ต้องตัดทิ้ง`
      + `ยังมี "${stray[0]}" อยู่ ซึ่งอาจเป็นส่วนหนึ่งของสูตร (เพดาน เงื่อนไข หรือชื่อตัวแปร) จึงไม่เดาให้ — `
      + 'ให้เขียนเป็นสูตรเดียวแล้ววางใหม่ เช่น IF(has_spouse = 1, 60000, 0) หรือ MIN(inc_40_3 * 0.5, 100000)'
  }
  const op = dropped.match(DROPPED_OPERATOR)
  if (op) {
    return `ไม่พบสูตรที่แน่ใจได้ในข้อความที่วาง — ส่วนที่ต้องตัดทิ้งมีเครื่องหมาย "${op[0].trim()}" `
      + 'ซึ่งเป็นการคำนวณ ไม่ใช่คำอธิบาย — ให้เขียนสูตรให้ครบแล้ววางเฉพาะตัวสูตร'
  }
  if (!CORE_IS_COMPUTATION.test(core)) {
    return `ไม่พบสูตรในข้อความที่วาง — ข้อความนี้เป็นคำอธิบาย ตัดคำอธิบายออกแล้วเหลือแค่ "${core}" `
      + 'ซึ่งเป็นค่าเดี่ยว ไม่ใช่การคำนวณ — ให้แปลงเป็นสูตรเองก่อน เช่น "ถ้ามีคู่สมรสหักได้ 60000" '
      + 'เขียนเป็น IF(has_spouse = 1, 60000, 0)'
  }
  return null
}

/**
 * Pull the formula out of a sentence.
 *
 * "สูตรคือ Min(num_parents, 4) * 30000 ครับ" and "The formula is: Min(ded_sso, 9000)" are ordinary
 * things to paste, and the old behaviour — a character offset blaming a Thai letter — reads as an
 * accusation about the author's language rather than a report about their formula.
 *
 * The rule is deliberately timid: only whole PROSE words, only from the two ends, the LARGEST surviving
 * core wins, and every candidate must clear `proseTrimRefusal`. Timid matters because throwing away
 * part of a formula is a silent wrong answer.
 * @returns {{core:?string, dropped:string, reason:?string}}  reason = why the best candidate was refused
 */
function extractFormulaFromProse(text) {
  const none = { core: null, dropped: '', reason: null }
  const words = text.split(/\s+/).filter((w) => w !== '')
  if (words.length < 2) return none
  let left = 0
  while (left < words.length && left < MAX_TRIM_WORDS && PROSE_WORD.test(words[left])) left += 1
  let right = 0
  while (right < words.length - left && right < MAX_TRIM_WORDS && PROSE_WORD.test(words[words.length - 1 - right])) right += 1

  let refused = null
  for (let drop = 1; drop <= left + right; drop += 1) {
    for (let i = Math.max(0, drop - right); i <= Math.min(drop, left); i += 1) {
      const j = drop - i
      const core = words.slice(i, words.length - j).join(' ')
      if (core === '' || !parses(core)) continue
      const dropped = words.slice(0, i).concat(words.slice(words.length - j)).join(' ')
      const why = proseTrimRefusal(core, dropped)
      if (why) { if (!refused) refused = why; continue }
      return { core, dropped, reason: null }
    }
  }
  return { core: null, dropped: '', reason: refused }
}

/**
 * Is there PROSE around this formula — English or Thai?
 *
 * TWO signals, and BOTH are needed:
 *   • two identifiers separated by nothing but a space (`the formula`, `rental expense`). That cannot
 *     occur in this grammar even once — a formula always has an operator, a comma or a paren between
 *     two names — so two such pairs is a sentence.
 *   • a non-ASCII letter outside a literal. Thai is illegal everywhere except inside quotes, so
 *     `สูตรคือ …` is unambiguously a lead-in; the ident-pair signal cannot see it (Thai words are not
 *     `[A-Za-z_]`), which is exactly the shape this user's LLM sessions produce.
 *
 * It gates BOTH the extraction and the NO_FORMULA refusal, and gating matters as much as extracting:
 * `CASE WHEN x = 1 THEN 60000 ELSE 0 END` is a DIALECT, not a sentence, and letting the extractor trim
 * its keywords away would hand back `1` — a formula the author never wrote, which is the one outcome
 * worse than a refusal. Refusing `and` inside an English paragraph as a boolean operator is the
 * mirror-image mistake, and this is the line between them.
 */
function looksLikeProse(text) {
  const masked = maskLiterals(text)
  // A CODE dialect can read like a sentence (`CASE WHEN … THEN … END`, an arrow function) but must
  // never be treated as one: trimming its keywords away would hand back whichever fragment happens to
  // parse. Those get a named diagnosis of their own instead.
  if (/\bCASE\b[\s\S]*?\bWHEN\b/i.test(masked) || masked.indexOf('=>') !== -1) return false
  if (/[^\p{Script=Latin}\p{Script=Common}\p{Script=Inherited}]/u.test(masked)) return true
  // Overlapping on purpose (a lookahead, not a consuming second half): `The formula is` is TWO pairs,
  // and a non-overlapping match counts it as one — i.e. calls a sentence a formula.
  const pairs = masked.match(/[A-Za-z_][A-Za-z0-9_]*[ \t]+(?=[A-Za-z_])/g)
  return (pairs ? pairs.length : 0) >= 2
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public: the normalizer
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Strip a leading list marker and/or an Excel `=` from the FRONT of `text`, reporting each.
 *
 * Split out of the main pipeline because it has to run TWICE. A marker only reaches offset 0 after the
 * prose around it is gone: an LLM's bulleted answer is a heading line and then the bullet
 * (`ค่าลดหย่อน…\n- MIN(ded_sso, 9000)`), so at step 10 the text still starts with the heading, and by
 * the time the prose strip has exposed the `- `, this repair has already run. The result was
 * `0 - MIN(ded_sso, 9000)` — the sign flipped, silently, on a formula the author pasted correctly.
 *
 * A list marker is not a minus sign: a marker is separated from the formula by whitespace, a real unary
 * minus is written against its operand (`-wht`). That is the same judgement at both call sites — the
 * second call is not a new rule, it is the first rule finally getting to see the text it applies to.
 * @param {string} text
 * @param {(code:string, message:string)=>void} repair
 * @returns {string}
 */
function stripLeadingMarkers(text, repair) {
  let out = text
  const marker = out.match(/^\s*([-*+•·‣▪]|\d{1,3}[.)])[ \t]+/)
  if (marker) {
    out = out.slice(marker[0].length)
    repair('LIST_MARKER', `ตัดสัญลักษณ์รายการ "${marker[1]}" ที่ต้นข้อความออก — `
      + 'ถ้าตั้งใจให้เป็นเครื่องหมายลบ ให้เขียนติดกับค่าที่ตามมา เช่น -wht')
  }
  // Excel/Sheets put a `=` at offset 0. There is no reading in which a formula STARTS with `=`, so
  // this is a one-character, zero-ambiguity repair — and pasting from a spreadsheet is common.
  const excel = out.match(/^\s*=(?![=<>])\s*/)
  if (excel) { out = out.slice(excel[0].length); repair('EXCEL_EQUALS', 'ตัดเครื่องหมาย = ที่ต้นสูตรแบบ Excel ออก') }
  return out
}

/**
 * Repair raw pasted text into an expression the grammar accepts.
 *
 * @param {string} raw   whatever landed in the textarea
 * @param {{outputVar?:string}} [opts]  the formula's CURRENT รหัสสูตร — used only to resolve the
 *        `name = …` ambiguity below; never written to and never required.
 * @returns {{
 *   ok:boolean,                    // safe to rebuild the canvas from `expression`
 *   expression:string,             // the repaired text ('' when nothing usable survived)
 *   canonical:string,              // `expression` as the GENERATOR would spell it (see canonicalizeExpression)
 *   assignedOutputVar:?string,     // the `name` of a stripped `name = …` — surfaced, never applied
 *   repairs:Array<{code,message}>, // what we changed, all of it
 *   problems:Array<{code,message}>,// why we cannot proceed
 *   identifiers:string[],          // every variable name in the result, for the off-catalog warning
 *   pathRoots:string[],            // every PATH root (a rule code), for the off-catalog warning
 * }}
 */
export function normalizePastedFormula(raw, opts = {}) {
  const repairs = []
  const problems = []
  const repair = (code, message) => repairs.push({ code, message })
  const fail = (code, message) => problems.push({ code, message })
  const empty = (extra) => ({
    ok: false, expression: '', canonical: '', assignedOutputVar: null, repairs, problems, identifiers: [], pathRoots: [], ...extra,
  })
  const refuse = (text, name) => ({
    ok: false, expression: text, canonical: '', assignedOutputVar: name || null, repairs, problems, identifiers: [], pathRoots: [],
  })

  let text = String(raw == null ? '' : raw)
  if (text.trim() === '') {
    fail('EMPTY', 'ยังไม่ได้ใส่สูตร — วางสูตรหรือพิมพ์สูตรลงในช่องนี้ก่อน')
    return empty()
  }

  // ── 1. markdown fence — when there is one, it is the answer ─────────────────
  // An LLM asked for "a formula" answers with a fenced block far more often than not, and wraps it in
  // a lead-in sentence and a trailing explanation. The fence's CONTENTS are authoritative; everything
  // outside is discarded wholesale (headings, `สูตรนี้จะ…`, English prose) rather than fed to the parser.
  const fence = extractFence(text)
  if (fence) {
    text = fence.inner
    repair('FENCE', 'ใช้เนื้อหาใน markdown code fence (```) เป็นสูตร'
      + (fence.outside ? ' และตัดข้อความอธิบายนอก fence ออก' : '')
      + (fence.extra ? ` (พบ ${fence.extra + 1} บล็อก — ใช้บล็อกแรก)` : ''))
  }

  // ── 2. quote glyphs → ASCII (BEFORE segmentation, see SMART_SINGLE) ─────────
  const straightS = text.replace(SMART_SINGLE, "'")
  if (straightS !== text) { repair('SMART_QUOTE', "แปลงอัญประกาศโค้ง/เต็มความกว้าง ‘ ’ ＇ เป็น ' ธรรมดา"); text = straightS }
  const straightD = text.replace(SMART_DOUBLE, '"')
  if (straightD !== text) { repair('SMART_QUOTE', 'แปลงอัญประกาศโค้ง “ ” เป็น " ธรรมดา'); text = straightD }
  // double → single, OUTSIDE existing literals: a pair of double quotes INSIDE a single-quoted value
  // (`'A"B"C'`) is content, and pairing it produced `'A'B'C'` — three literals, a repair line claiming
  // it helped, and a parse error pointing at the wrong place.
  // `"…"` is now a LEGAL literal (the nested-path grammar writes `subTypeCode = "BUILDING_30"`), so this
  // is no longer a repair that rescues an illegal spelling — it is a NORMALISATION onto the one form the
  // generator emits, and it must be provably meaning-preserving to be allowed at all. It is, but only
  // while the content carries no `'`: swapping the delimiters around a value that contains the OTHER
  // quote would end the literal early and split one value into two. That content is illegal anyway
  // (the literal charset has no quote in it), so it is LEFT ALONE for the parser to name precisely,
  // rather than mangled here into a shape whose error points somewhere else.
  const singled = mapOutside(text, (t) => t.replace(/"([^"\n]*)"/g, (all, inner) => (inner.indexOf("'") === -1 ? "'" + inner + "'" : all)))
  if (singled !== text) { repair('DOUBLE_QUOTE', "เขียนข้อความด้วย double quote เป็น single quote ('FEMALE') — ความหมายเท่าเดิม (ตัวแก้ไขเก็บเป็น single quote เสมอ)"); text = singled }

  // ── 3. invisible characters ─────────────────────────────────────────────────
  // Outside a literal they are a baffling "unexpected character" nobody can see; INSIDE one they are
  // illegal content, so the parser would reject a literal that looks perfect on screen. Both are
  // repaired — and the inside case NAMES the literal, because that is the author's data changing.
  const outNbsp = mapOutside(text, (t) => t.replace(INVISIBLE_TO_SPACE, ' '))
  if (outNbsp !== text) { repair('NBSP', 'แทนช่องว่างพิเศษ (NBSP/ช่องว่างกว้าง) ด้วยช่องว่างปกติ'); text = outNbsp }
  const outZw = mapOutside(text, (t) => t.replace(INVISIBLE_TO_EMPTY, ''))
  if (outZw !== text) { repair('ZERO_WIDTH', 'ตัดอักขระล่องหน (zero-width/BOM) ออก'); text = outZw }
  const inv = mapInsideLiterals(text, (t) => t.replace(INVISIBLE_TO_SPACE, ' ').replace(INVISIBLE_TO_EMPTY, ''))
  if (inv.changed.length) {
    text = inv.text
    inv.changed.forEach((c) => repair('LITERAL_INVISIBLE',
      `ตัดอักขระล่องหนในข้อความ '${c.from}' ออก — ค่าที่จะใช้เทียบคือ '${c.to}' (ตรวจว่าตรงกับค่าจริงในระบบ)`))
  }
  // Backticks: OUTSIDE only. One inside a literal is illegal content, and the parser's message quotes
  // the literal back — far better than silently deleting a character from the author's value.
  const noTick = mapOutside(text, (t) => t.replace(/`/g, ''))
  if (noTick !== text) { repair('BACKTICK', 'ตัดเครื่องหมาย ` ออก'); text = noTick }

  // ── 4. comments (quote-aware): // … , # … , /* … */ ─────────────────────────
  const com = stripComments(text)
  if (com.text !== text) {
    repair('COMMENT', `ตัดคอมเมนต์${com.line ? ' // หรือ #' : ''}${com.block ? ' และ /* … */' : ''} ออก`)
    text = com.text
  }

  // ── 5. HTML entities (outside literals) ─────────────────────────────────────
  const entities = mapOutside(text, (t) => HTML_ENTITIES.reduce((acc, [re, ch]) => acc.replace(re, ch), t))
  if (entities !== text) { repair('HTML_ENTITY', 'แปลง HTML entity (&gt; &lt; &amp;) กลับเป็นเครื่องหมายปกติ'); text = entities }

  // ── 6. everything from here is OUTSIDE-LITERALS ONLY (rule 2) ───────────────
  const debracketed = mapOutside(text, (t) => t.replace(BRACKET_IDENT_RE, '$1'))
  if (debracketed !== text) {
    repair('BRACKET', 'แปลงชื่อตัวแปรแบบ [NAME] (รูปแบบใน POC-Calc-Blueprint) เป็น NAME')
    text = debracketed
  }
  const asciiOps = mapOutside(text, (t) => t
    .replace(DASH_TO_MINUS, '-')
    .replace(TIMES_TO_STAR, '*')
    .replace(DIVIDE_TO_SLASH, '/'))
  if (asciiOps !== text) { repair('UNICODE_OP', 'แปลงเครื่องหมาย − × ÷ (และ dash ยาว) เป็น - * /'); text = asciiOps }
  const ascii = mapOutside(text, (t) => t.replace(FULLWIDTH_ASCII, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0)))
  if (ascii !== text) { repair('FULLWIDTH', 'แปลงอักขระเต็มความกว้าง （） ＋ － ＊ ／ ， เป็นแบบ ASCII'); text = ascii }

  // ── 7. numbers ──────────────────────────────────────────────────────────────
  // The numeric comma is FIRST and is the reason this section exists: the blueprint prose writes every
  // cap as `100,000`, an LLM copies that formatting, and `MIN(x, 100,000)` parses cleanly as
  // MIN(x, 100, 0) — the cap becomes zero and nothing anywhere objects. It runs BEFORE the whitespace
  // collapse and is whitespace-tolerant by construction, so `100 ,000` cannot slip past it.
  const commas = repairNumericCommas(text)
  if (commas.repair) { repairs.push(commas.repair); text = commas.text }
  if (commas.problems.length) {
    // An unreadable number cannot be repaired further, and everything downstream (prose trimming, the
    // parser) would only add noise on top of the one thing that must be fixed.
    commas.problems.forEach((p) => problems.push(p))
    return refuse(text, null)
  }
  const noUnderscore = mapOutside(text, (t) => t.replace(NUMERIC_UNDERSCORE, (n) => n.replace(/_/g, '')))
  if (noUnderscore !== text) { repair('NUMBER_UNDERSCORE', 'ตัดขีดล่างคั่นหลักในตัวเลขออก (300_000 → 300000)'); text = noUnderscore }
  const noSci = mapOutside(text, (t) => t.replace(SCI_NOTATION, (all, mant, exp) => {
    const v = Number(all)
    // Only when the expansion is EXACT and writable in plain digits — otherwise leave it for the
    // NUMBER_RANGE refusal, which explains why rather than persisting something unreadable.
    return (Number.isFinite(v) && String(v).indexOf('e') === -1) ? String(v) : all
  }))
  if (noSci !== text) { repair('SCI_NOTATION', 'แปลงเลขยกกำลังสิบ (5e6) เป็นตัวเลขเต็ม (5000000)'); text = noSci }
  const noBaht = mapOutside(text, (t) => t.replace(/\u0E3F/g, ''))
  if (noBaht !== text) { repair('CURRENCY', 'ตัดสัญลักษณ์สกุลเงิน ฿ ออก (ใส่เฉพาะตัวเลข)'); text = noBaht }

  // ── 8. operator spellings from other languages ──────────────────────────────
  const noStrictEq = mapOutside(text, (t) => t.replace(/===/g, '==').replace(/!==/g, '!='))
  if (noStrictEq !== text) { repair('STRICT_EQ', 'แปลง === / !== เป็น == / !='); text = noStrictEq }
  const noDangling = mapOutside(text, (t) => t.replace(/,(\s*)\)/g, '$1)'))
  if (noDangling !== text) { repair('TRAILING_COMMA', 'ตัดจุลภาคที่ค้างอยู่หน้าวงเล็บปิดออก'); text = noDangling }
  // `Math.max(…)` — a namespace prefix on functions that exist here by the same name AND the same
  // arity, so dropping it is the one dialect repair that invents nothing. Deliberately limited to the
  // three we have: `Math.abs` keeps its prefix and is reported as an unknown function, which is the
  // truth, rather than being turned into something that looks supported.
  const noMathNs = mapOutside(text, (t) => t.replace(/\bMath\s*\.\s*(min|max|round)\s*\(/gi, '$1('))
  if (noMathNs !== text) { repair('MATH_NS', 'ตัดคำนำหน้า Math. ออก (Math.max → MAX)'); text = noMathNs }

  // ── 9. one editor holds ONE formula ─────────────────────────────────────────
  // Pasting a whole blueprint SECTION is a genuine mistake, not a syntax error: collapsing it to one
  // line would produce `a = x b = y` and a parse error naming a token that explains nothing.
  const heads = topLevelAssignmentHeads(text)
  if (heads.length > 1) {
    fail('MULTI_FORMULA', `พบ ${heads.length} สูตรในข้อความที่วาง (${heads.join(', ')}) — `
      + 'หน้านี้แก้ได้ครั้งละหนึ่งสูตร ให้วางทีละสูตร')
    return empty()
  }

  // ── 10. leading markers, trailing `;`, whitespace collapse ──────────────────
  text = stripLeadingMarkers(text, repair)

  // The full stop that ends a SENTENCE, not a number: `…- wht).` NARROWED to a `)` when `.` became the
  // path separator: a trailing dot after a NAME is now far more likely a TRUNCATED PATH
  // (`INCOME_40_5.subItems[].`) than punctuation, and deleting it would turn a formula the author cut
  // short into a different, complete-looking one. After a `)` there is no such reading — a path
  // segment can never follow a closing paren — so that case stays a repair.
  const period = mapOutside(text, (t) => t.replace(/(\))\s*\.\s*$/, '$1'))
  if (period !== text) { repair('SENTENCE_PERIOD', 'ตัดจุด "." ท้ายประโยคออก'); text = period }
  const desemi = mapOutside(text, (t) => t.replace(/;+\s*$/, ''))
  if (desemi !== text) { repair('SEMICOLON', 'ตัดเครื่องหมาย ; ท้ายสูตรออก'); text = desemi }
  const collapsed = mapOutside(text, (t) => t.replace(/\s+/g, ' ')).trim()
  if (collapsed !== text) { repair('WHITESPACE', 'รวมการขึ้นบรรทัดใหม่/ช่องว่างซ้ำให้เหลือช่องว่างเดียว'); text = collapsed }

  if (text === '') {
    fail('EMPTY', 'ไม่เหลือสูตรหลังตัดคอมเมนต์/เครื่องหมายที่ไม่ใช่สูตรออก')
    return empty()
  }

  // ── 11. the assignment head ─────────────────────────────────────────────────
  const selfName = String(opts.outputVar || '').trim()
  let assignedOutputVar = null
  const head1 = stripAssignmentHead(text, selfName)
  if (head1.repair) { text = head1.text; assignedOutputVar = head1.name; repairs.push(head1.repair) }
  if (head1.problem) { problems.push(head1.problem); return refuse(text, null) }

  // ── 12. the formula inside a sentence ───────────────────────────────────────
  // Only when there IS a sentence (see looksLikeProse): trimming words off a DIALECT — SQL's
  // `CASE WHEN … END`, a JS arrow function — would quietly hand back whichever fragment happens to
  // parse, which is a formula the author never wrote.
  if (!parses(text) && looksLikeProse(text)) {
    const ex = extractFormulaFromProse(text)
    if (ex.core) {
      text = ex.core
      repair('PROSE', `ตัดข้อความอธิบายรอบสูตรออก ("${ex.dropped}") — ใช้เฉพาะ "${ex.core}" เป็นสูตร`)
      // A LIST MARKER can only surface after the sentence is gone too — an LLM's bulleted answer is a
      // heading line and then the bullet, so at step 10 the text still began with the heading.
      text = stripLeadingMarkers(text, repair)
      // The head can only surface AFTER the sentence is gone (`3. alw_child = …`), so try once more.
      const head2 = stripAssignmentHead(text, selfName)
      if (head2.repair) { text = head2.text; assignedOutputVar = assignedOutputVar || head2.name; repairs.push(head2.repair) }
      if (head2.problem) { problems.push(head2.problem); return refuse(text, assignedOutputVar) }
    } else {
      // Nothing formula-shaped survived — or something did, but taking it would have INVENTED a
      // formula (see proseTrimRefusal), which is the one outcome worse than a refusal. This MUST NOT
      // fall through to diagnose/parse either: an English paragraph gets reported as an unsupported
      // `and`, and a Thai one as an illegal letter — both read as "your formula was nearly right",
      // which is the opposite of the truth.
      fail('NO_FORMULA', ex.reason
        || ('ไม่พบสูตรในข้อความที่วาง — วางเฉพาะตัวสูตร เช่น MIN(inc_40_3 * 0.5, 100000) '
          + `(ใช้ได้เฉพาะตัวเลข ชื่อตัวแปร เครื่องหมาย + - * / ( ) , การเปรียบเทียบ และฟังก์ชัน ${FN_LIST_TH})`))
      return refuse(text, assignedOutputVar)
    }
  }

  // ── 13. what we could not repair ────────────────────────────────────────────
  diagnose(text).forEach((p) => problems.push(p))
  if (text.length > MAX_EXPRESSION_LEN) {
    fail('TOO_LONG', `สูตรยาว ${text.length} อักขระ เกินเพดาน ${MAX_EXPRESSION_LEN} — ตัดเป็นสูตรย่อยแล้วอ้างถึงกัน`)
  }
  if (problems.length) return refuse(text, assignedOutputVar)

  // ── 14. the parser is the final judge ───────────────────────────────────────
  let ast
  try {
    ast = parseExpression(text)
  } catch (err) {
    fail('PARSE', describeParseError(err, text))
    return refuse(text, assignedOutputVar)
  }
  // The two STRUCTURAL `[]` rules (uncorrelated loops · a function wrapping the whole iterated
  // computation with no explicit SUM). Shared with the generator and the lint, so the paste preview
  // and the canvas cannot disagree about what is savable — and reported HERE, before Apply, because
  // the whole point of this dialog is that the author learns before their blocks are replaced.
  analyzeIteration(ast).forEach((m) => fail('ITERATION', m))
  if (problems.length) return refuse(text, assignedOutputVar)

  // ── 15. compile it exactly the way APPLY will ───────────────────────────────
  // `ok:true` used to mean "the grammar accepts it", which is weaker than "this is a formula":
  // `'FEMALE'` previewed green, Apply was enabled, the canvas was destroyed, and only then did the
  // editor report สูตรต้องคืนค่าเป็นตัวเลข with the author's previous work already gone. Going through
  // the real `tax_formula_result` anchor is what makes the preview and the post-apply truth agree.
  const built = canonicalizeThroughAnchor(text)
  if (built.errors.length || built.canonical === '') {
    built.errors.forEach((m) => fail('COMPILE', m))
    if (!built.errors.length) fail('COMPILE', 'สร้างบล็อกจากข้อความนี้ไม่ได้ — ตรวจรูปแบบสูตรอีกครั้ง')
    return refuse(text, assignedOutputVar)
  }
  // …and the canonical must be READABLE BACK. The generator writes numbers with `String(v)`, which
  // is exponent notation past 1e21 or below 1e-6 — a spelling this project's own tokenizer cannot
  // parse, so the formula would save fine and be unopenable the first time the stored graph is absent
  // or drifted (bundle import, version clone). Cheap invariant, caught a real defect.
  if (!parses(built.canonical) || canonicalizeThroughAnchor(built.canonical).canonical !== built.canonical) {
    fail('CANONICAL_UNSTABLE', `สูตรนี้เขียนกลับเป็นข้อความแล้วอ่านซ้ำไม่ได้ (ได้ "${built.canonical}") — `
      + describeUnstableCause(built.canonical))
    return refuse(text, assignedOutputVar)
  }

  // ── 16. the re-spelling the author is about to READ ─────────────────────────
  // The dialog previews `canonical`, so `-wht + tax_before_credit` reads back as `0 - wht + …` and
  // `IF(x = 1, …)` as `IF(x == 1, …)`. The MEANING is identical — this is not the silent-wrong class —
  // but rule 1 is that the author is told about every change they SEE, and an unexplained difference
  // between what they typed and what the box shows is indistinguishable from a bug at a glance.
  // Naming the specific re-spellings (rather than a bare "this was reformatted") is what keeps the
  // line worth reading on the many pastes where it fires.
  const respell = describeRespelling(text, built.canonical)
  if (respell) {
    repair('CANONICAL_FORM', `สูตรจะถูกเก็บ/แสดงในรูปแบบมาตรฐานของตัวแก้ไข: "${built.canonical}" — `
      + `ความหมายเท่าเดิม (${respell})`)
  }

  return {
    ok: true,
    expression: text,
    canonical: built.canonical,
    assignedOutputVar,
    repairs,
    problems,
    identifiers: collectIdentifiers(text),
    pathRoots: collectPathRoots(text),
  }
}

/**
 * The specific ways the generator re-spells `expression` as `canonical`, or '' when they agree.
 *
 * Every item here is meaning-preserving by construction — they are the spellings the grammar accepts
 * but the generator does not emit. Anything the author would have to think about (a number, a name, an
 * operand order) is NOT in this list, and cannot be: those differences are repairs or refusals long
 * before this point.
 */
function describeRespelling(expression, canonical) {
  if (canonical === expression) return ''
  const masked = maskLiterals(expression)
  const why = []
  // Numbers FIRST: `0.30` → `0.3`, `.5` → `0.5`. Meaning-preserving, but a number is the one thing an
  // author re-reads digit by digit, so "reformatted" is not an acceptable description of it.
  const renum = numberTokens(masked).find((t) => Number.isFinite(Number(t.text)) && String(Number(t.text)) !== t.text)
  if (renum) why.push(`ตัวเลข ${renum.text} เขียนเป็น ${String(Number(renum.text))} (ค่าเท่ากัน)`)
  if (/\b(?:min|max|round|if|ifs|sum)\s*\(/.test(masked)
    && !/\b(?:min|max|round|if|ifs|sum)\s*\(/.test(maskLiterals(canonical))) {
    why.push('ชื่อฟังก์ชันเขียนเป็นตัวพิมพ์ใหญ่')
  }
  if (/(?<![<>=!])=(?![=<>])/.test(masked)) why.push('เครื่องหมาย = เขียนเป็น ==')
  if (masked.indexOf('<>') !== -1) why.push('<> เขียนเป็น !=')
  if (/(^|[(,+\-*/<>=])\s*-/.test(masked) && canonical.indexOf('0 - ') !== -1) {
    why.push('เครื่องหมายลบหน้าค่า เขียนเป็น 0 - …')
  }
  if (!why.length) why.push('จัดช่องว่างและวงเล็บใหม่ตามรูปแบบของตัวแก้ไข')
  return why.join(', ')
}

// ──────────────────────────────────────────────────────────────────────────────
//  Public helpers the view needs
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Every VARIABLE name in `expression` (function names and any
 * literal content excluded), de-duplicated, in first-seen order. The view classifies these against the
 * live catalog and NAMES the unknown ones before the author applies: an LLM invents variable names
 * constantly, and an identifier that is not in the catalog lands on the canvas wearing a "!" — that is
 * the common case here, not the edge case, so it must be said before the rebuild, not discovered after.
 * @param {string} expression
 * @returns {string[]}
 */
export function collectIdentifiers(expression) {
  const src = String(expression == null ? '' : expression)
  const out = []
  const seen = new Set()
  const push = (n) => { if (n && !seen.has(n)) { seen.add(n); out.push(n) } }
  // Through the REAL tokenizer, not a regex over the text. A nested path is ONE name
  // (`INCOME_40_5.subItems[].amount`), and the regex saw three — so the view's off-catalog warning
  // named `subItems` and `amount` as unknown variables, which are not variables and are not unknown.
  // TRUE/FALSE are keywords, not names, and are skipped for the same reason function names are.
  try {
    const toks = tokenize(src)
    toks.forEach((t, i) => {
      if (t.type === 'path') { push(t.value); return }
      if (t.type !== 'ident') return
      if (toks[i + 1] && toks[i + 1].type === 'lparen') return // a function call, not a variable
      if (/^(?:TRUE|FALSE)$/i.test(t.value)) return
      push(t.value)
    })
    return out
  } catch (_err) {
    // Unparseable text still has to yield SOMETHING (the view warns before the parser has had its
    // say on a half-repaired paste), so fall back to the old scan rather than returning nothing.
    const masked = maskLiterals(src)
    IDENT_G.lastIndex = 0
    let m
    while ((m = IDENT_G.exec(masked)) !== null) {
      let j = m.index + m[0].length
      while (j < masked.length && /\s/.test(masked[j])) j += 1
      if (masked[j] === '(') continue
      push(m[0])
    }
    return out
  }
}

/**
 * Every distinct path ROOT in `expression`, in first-seen order — `INCOME_40_5` of
 * `INCOME_40_5.subItems[].items[].amount`.
 *
 * A root is a RULE CODE and nothing else: the engine resolves the path against that rule's form data,
 * so a root naming a rule this version does not have resolves to nothing, contributes 0, and reports
 * NOTHING — a silent zero on a real deduction. The view compares these against the live rule list and
 * says so before the author applies. (This replaces `collectScopes`, which read the same warning off a
 * retired SUMITEMS scope literal.)
 *
 * Through the REAL tokenizer, never a regex: a literal spelling `A.b[].c` is text, not a path.
 * @param {string} expression
 * @returns {string[]}
 */
export function collectPathRoots(expression) {
  const out = []
  const seen = new Set()
  let toks
  try { toks = tokenize(String(expression == null ? '' : expression)) } catch (_err) { return out }
  toks.forEach((t) => {
    if (!t || t.type !== 'path') return
    const root = pathRoot(t.value)
    if (!root || seen.has(root.toLowerCase())) return
    seen.add(root.toLowerCase())
    out.push(root)
  })
  return out
}

/**
 * Compile `expression` the way the APPLY path does: parse to blocks, plug them into a real
 * `tax_formula_result` anchor, and read the result back through `workspaceToExpression`.
 *
 * Going through the anchor (rather than compiling the bare root with `blockToNCalc`) is what makes
 * `ok:true` mean "a valid FORMULA" rather than "the grammar accepts it": the anchor-level rules —
 * above all "a formula must return a number" — live in `compileExpression`, and skipping them let the
 * preview go green on text the editor rejects immediately AFTER destroying the canvas.
 * @param {string} expression  an expression that already parses
 * @returns {{canonical:string, errors:string[]}}
 */
function canonicalizeThroughAnchor(expression) {
  let ws = null
  try {
    ws = new Blockly.Workspace()
    const root = ncalcToBlocks(ws, expression, {})
    const anchor = ws.newBlock(TAX_FORMULA_RESULT_TYPE)
    if (root && root.outputConnection) anchor.getInput('VALUE').connection.connect(root.outputConnection)
    const out = workspaceToExpression(ws)
    return { canonical: out.expression, errors: out.errors }
  } catch (err) {
    return { canonical: '', errors: [(err && err.message) ? String(err.message) : 'build error'] }
  } finally {
    if (ws) { try { ws.dispose() } catch (_e) { /* headless — ignore */ } }
  }
}

/**
 * `expression` re-spelled the way the GENERATOR would emit it — parse to blocks, compile back.
 *
 * This exists so the view can honestly answer "is the canvas what you pasted?". The grammar accepts
 * spellings the generator never produces (`if(` vs `IF(`, bare `=` vs `==`, `<>` vs `!=`, its own
 * parenthesisation), so comparing the rebuilt canvas against the RAW pasted text would flag a perfect
 * rebuild on nearly every blueprint paste — an alarm that fires always is an alarm that says nothing.
 * Canonicalising through the very same pair (`ncalcToBlocks` → generator) that the apply path uses
 * keeps the check meaningful: what is left after this can only be a REAL structural difference.
 * @param {string} expression  an expression that already parses
 * @returns {string} '' when it does not compile (the caller already reported that)
 */
export function canonicalizeExpression(expression) {
  return canonicalizeThroughAnchor(expression).canonical
}

export default {
  ALLOWED_FUNCTIONS,
  normalizePastedFormula,
  collectIdentifiers,
  collectPathRoots,
  canonicalizeExpression,
}
